import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilterPill from './FilterPill';
import OptionListPopover from './OptionListPopover';
import DateRangePopover from './DateRangePopover';
import MetricFilterPopover from './MetricFilterPopover';
import { TagFilterMenu } from '../TagFilterDropdown';
import {
    SOURCE_OPTIONS,
    METRIC_COLUMN_OPTIONS,
    METRIC_OPERATOR_OPTIONS,
    COMBINE_DATA_OPTIONS,
    EMPTY_METRIC_FILTER,
    findOptionLabel,
    formatDateRange,
} from './filterOptions';
import './DashboardFilterRail.css';

const MORE_FILTERS_STORAGE_KEY = 'dashboardShowMoreFilters';

const readShowMoreFilters = () => {
    try {
        const saved = localStorage.getItem(MORE_FILTERS_STORAGE_KEY);
        return saved !== null ? JSON.parse(saved) : false;
    } catch {
        return false;
    }
};

const DATA_RANGE_NOTE = "Views can't be filtered by date — YouTube only provides lifetime view counts.";
const PUBLISHED_RANGE_NOTE = 'Only resources published inside this range are shown.';

const summariseTags = (selectedTags) => {
    if (selectedTags.length === 0) return '';
    if (selectedTags.length <= 2) return selectedTags.join(', ');
    return `${selectedTags.length} tags`;
};

/**
 * Pill-style filter rail for the dashboard table.
 *
 * Primary rail: Offer, Source, Data between, plus any *active* secondary
 * filter (promoted so an applied filter is never hidden), then "More filters"
 * and "Clear filters".
 * Secondary rail (toggled by More filters): Published between, Metric filter,
 * Tags, Combine data. A promoted filter leaves a dimmed placeholder behind.
 * Filters flagged `promotable: false` (Combine data) always stay in the
 * secondary rail as a plain selector, even when active.
 */
const DashboardFilterRail = ({
    // Source
    filterType,
    onFilterTypeChange,
    // Offer
    offers,
    selectedOffer,
    onSelectedOfferChange,
    // Data between
    startDate,
    endDate,
    onDateRangeChange,
    onClearDateRange,
    // Published between
    publishedStartDate,
    publishedEndDate,
    onPublishedRangeChange,
    onClearPublishedRange,
    // Metric
    dataFilter,
    onDataFilterChange,
    // Tags
    allTags,
    selectedTags,
    onSelectedTagsChange,
    // Combine data
    aggregateVideoData,
    onAggregateVideoDataChange,
    // Global
    hasActiveFilters,
    onClearAll,
    resultCount,
    /** Table-level actions (e.g. Customize columns, Download CSV) rendered at the far right. */
    actions,
}) => {
    const [openFilter, setOpenFilter] = useState(null);
    const [showMoreFilters, setShowMoreFilters] = useState(readShowMoreFilters);

    useEffect(() => {
        localStorage.setItem(MORE_FILTERS_STORAGE_KEY, JSON.stringify(showMoreFilters));
    }, [showMoreFilters]);

    const closePopover = useCallback(() => setOpenFilter(null), []);
    const pillProps = (key) => ({
        isOpen: openFilter === key,
        onOpen: () => setOpenFilter(key),
        onClose: closePopover,
    });

    const offerOptions = useMemo(
        () => [{ value: 'all', label: 'All offers' }, ...offers.map((offer) => ({ value: offer.name, label: offer.name }))],
        [offers],
    );

    const metricValue = dataFilter.column && dataFilter.value !== ''
        ? `${findOptionLabel(METRIC_COLUMN_OPTIONS, dataFilter.column)} ${
            METRIC_OPERATOR_OPTIONS.find((o) => o.value === dataFilter.operator)?.symbol ?? '>'
        } ${dataFilter.value}`
        : '';

    // ---- Primary filters -------------------------------------------------

    const offerPill = (
        <FilterPill
            key="offer"
            label="Offer"
            value={selectedOffer !== 'all' ? selectedOffer : ''}
            onClear={() => onSelectedOfferChange('all')}
            {...pillProps('offer')}
        >
            <OptionListPopover
                options={offerOptions}
                value={selectedOffer}
                searchPlaceholder="Search offers"
                onSelect={(value) => {
                    onSelectedOfferChange(value);
                    closePopover();
                }}
            />
        </FilterPill>
    );

    const sourcePill = (
        <FilterPill
            key="source"
            label="Source"
            value={filterType !== 'all' ? findOptionLabel(SOURCE_OPTIONS, filterType) : ''}
            onClear={() => onFilterTypeChange('all')}
            {...pillProps('source')}
        >
            <OptionListPopover
                options={SOURCE_OPTIONS}
                value={filterType}
                searchPlaceholder="Search sources"
                onSelect={(value) => {
                    onFilterTypeChange(value);
                    closePopover();
                }}
            />
        </FilterPill>
    );

    const dataRangePill = (
        <FilterPill
            key="dataRange"
            label="Data between"
            value={formatDateRange(startDate, endDate)}
            onClear={onClearDateRange}
            {...pillProps('dataRange')}
        >
            <DateRangePopover
                startDate={startDate}
                endDate={endDate}
                onChange={onDateRangeChange}
                onComplete={closePopover}
                note={DATA_RANGE_NOTE}
            />
        </FilterPill>
    );

    // ---- Secondary filters -----------------------------------------------

    const secondaryFilters = [
        {
            key: 'publishedRange',
            label: 'Published between',
            active: Boolean(publishedStartDate || publishedEndDate),
            render: () => (
                <FilterPill
                    key="publishedRange"
                    label="Published between"
                    value={formatDateRange(publishedStartDate, publishedEndDate)}
                    onClear={onClearPublishedRange}
                    {...pillProps('publishedRange')}
                >
                    <DateRangePopover
                        startDate={publishedStartDate}
                        endDate={publishedEndDate}
                        onChange={onPublishedRangeChange}
                        onComplete={closePopover}
                        note={PUBLISHED_RANGE_NOTE}
                    />
                </FilterPill>
            ),
        },
        {
            key: 'metric',
            label: 'Metric filter',
            active: Boolean(metricValue),
            render: () => (
                <FilterPill
                    key="metric"
                    label="Metric filter"
                    value={metricValue}
                    onClear={() => onDataFilterChange({ ...EMPTY_METRIC_FILTER })}
                    popoverWidth={300}
                    {...pillProps('metric')}
                >
                    <MetricFilterPopover
                        dataFilter={dataFilter}
                        onChange={onDataFilterChange}
                        onClose={closePopover}
                    />
                </FilterPill>
            ),
        },
        ...(allTags.length > 0 ? [{
            key: 'tags',
            label: 'Tags',
            active: selectedTags.length > 0,
            render: () => (
                <FilterPill
                    key="tags"
                    label="Tags"
                    value={summariseTags(selectedTags)}
                    onClear={() => onSelectedTagsChange([])}
                    {...pillProps('tags')}
                >
                    <TagFilterMenu
                        embedded
                        tags={allTags}
                        selectedTags={selectedTags}
                        onChange={onSelectedTagsChange}
                    />
                </FilterPill>
            ),
        }] : []),
        {
            key: 'combine',
            label: 'Combine video, QR & pinned data',
            active: Boolean(aggregateVideoData),
            promotable: false,
            render: () => (
                <FilterPill
                    key="combine"
                    label="Combine video, QR & pinned data"
                    value={aggregateVideoData ? 'Yes' : ''}
                    onClear={() => onAggregateVideoDataChange(false)}
                    popoverWidth={300}
                    {...pillProps('combine')}
                >
                    <OptionListPopover
                        options={COMBINE_DATA_OPTIONS}
                        value={aggregateVideoData ? 'yes' : 'no'}
                        searchable={false}
                        onSelect={(value) => {
                            onAggregateVideoDataChange(value === 'yes');
                            closePopover();
                        }}
                    />
                </FilterPill>
            ),
        },
    ];

    const promotedFilters = secondaryFilters.filter((filter) => filter.active && filter.promotable !== false);

    return (
        <div className="filter-rail-group">
            <div className="filter-rail" role="toolbar" aria-label="Filters">
                {offerPill}
                {sourcePill}
                {dataRangePill}
                {promotedFilters.map((filter) => filter.render())}

                <button
                    type="button"
                    className={`filter-pill${showMoreFilters ? ' is-expanded' : ''}`}
                    onClick={() => setShowMoreFilters((prev) => !prev)}
                    aria-expanded={showMoreFilters}
                    aria-controls="dashboard-secondary-filters"
                >
                    <span className="filter-rail__toggle-icon" aria-hidden="true">
                        {showMoreFilters ? '−' : '+'}
                    </span>
                    More filters
                </button>

                <div className="filter-rail__right">
                    {typeof resultCount === 'number' && (
                        <span className="filter-rail__count">
                            {resultCount} {resultCount === 1 ? 'resource' : 'resources'}
                        </span>
                    )}
                    {hasActiveFilters && (
                        <button type="button" className="filter-rail__clear" onClick={onClearAll}>
                            Clear filters
                        </button>
                    )}
                    {actions && (
                        <div className="filter-rail__actions" role="group" aria-label="Table actions">
                            {actions}
                        </div>
                    )}
                </div>
            </div>

            {showMoreFilters && (
                <div
                    id="dashboard-secondary-filters"
                    className="filter-rail filter-rail--secondary"
                    role="toolbar"
                    aria-label="More filters"
                >
                    {secondaryFilters.map((filter) => (
                        filter.active && filter.promotable !== false ? (
                            <span key={filter.key} className="filter-pill is-ghost" aria-hidden="true">
                                <span className="filter-rail__toggle-icon">↑</span>
                                {filter.label}
                            </span>
                        ) : filter.render()
                    ))}
                </div>
            )}
        </div>
    );
};

export default DashboardFilterRail;
